import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Users,
  Heart,
  Wallet,
  Shield,
  ArrowRight,
  Loader2,
  AlertCircle,
  UserPlus,
  HandHeart,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

interface WalletInviteData {
  wallet: {
    id: string;
    patientName: string;
    organizationName?: string;
    walletType: string;
    category: string;
    description?: string;
  };
  owner: {
    firstName: string;
    lastName: string;
  };
}

function getWalletTypeLabel(walletType: string): string {
  const labels: Record<string, string> = {
    family: "Family Wallet",
    community: "Community Wallet",
    organization: "Organization Wallet",
    personal: "Personal Wallet",
  };
  return labels[walletType] || walletType;
}

function getWalletTypeIcon(walletType: string) {
  switch (walletType) {
    case "family":
      return Users;
    case "community":
      return Heart;
    case "organization":
      return Shield;
    default:
      return Wallet;
  }
}

export default function WalletInvitePage() {
  usePageTitle(
    "Join Health Wallet",
    "Accept an invitation to join a HealthWallet and participate in community healthcare funding."
  );
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [joinAs, setJoinAs] = useState<"contributor" | "beneficiary" | null>(
    null
  );

  const {
    data,
    isLoading,
    error,
  } = useQuery<WalletInviteData>({
    queryKey: ["/api/wallet-join", token],
    enabled: !!token,
  });

  const joinMutation = useMutation({
    mutationFn: async (role: "contributor" | "beneficiary") => {
      const response = await apiRequest("POST", `/api/wallet-join/${token}`, {
        joinAs: role,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/wallet"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/full-wallet-access"] });
      toast({
        title: "Welcome!",
        description: "You have successfully joined the wallet.",
      });
      setLocation("/wallet");
    },
    onError: (err: Error) => {
      const message = err.message || "Failed to join wallet. Please try again.";
      toast({
        title: "Unable to Join",
        description: message.includes("already")
          ? "You are already a member of this wallet."
          : message,
        variant: "destructive",
      });
    },
  });

  const handleJoin = () => {
    if (joinAs) {
      joinMutation.mutate(joinAs);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <div className="text-center mb-8">
            <Skeleton className="h-16 w-16 rounded-full mx-auto mb-4" />
            <Skeleton className="h-8 w-64 mx-auto mb-2" />
            <Skeleton className="h-5 w-40 mx-auto" />
          </div>
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="text-center py-12">
            <CardContent className="space-y-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mx-auto">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <h1
                className="text-2xl font-bold"
                data-testid="text-error-title"
              >
                Invalid Invite Link
              </h1>
              <p
                className="text-muted-foreground"
                data-testid="text-error-message"
              >
                This invite link may be invalid, expired, or has already been
                used. Please ask the wallet owner for a new invitation.
              </p>
              <Button
                variant="outline"
                onClick={() => setLocation("/")}
                data-testid="button-return-home"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Return Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { wallet, owner } = data;
  const TypeIcon = getWalletTypeIcon(wallet.walletType);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12">
      <div className="container mx-auto px-4 max-w-lg space-y-8">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-2">
            <TypeIcon className="h-8 w-8 text-primary" />
          </div>
          <h1
            className="text-3xl font-bold"
            data-testid="text-wallet-name"
          >
            {wallet.patientName}
          </h1>
          <Badge variant="secondary" data-testid="badge-wallet-type">
            {getWalletTypeLabel(wallet.walletType)}
          </Badge>
          <p
            className="text-muted-foreground"
            data-testid="text-invited-by"
          >
            Invited by {owner.firstName} {owner.lastName}
          </p>
          {wallet.description && (
            <p
              className="text-muted-foreground max-w-md mx-auto"
              data-testid="text-wallet-description"
            >
              {wallet.description}
            </p>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <UserPlus className="h-5 w-5" />
              Join This Wallet
            </CardTitle>
            <CardDescription>
              You've been invited to join{" "}
              <span className="font-medium text-foreground">
                {wallet.patientName}
              </span>
              . Choose how you'd like to participate.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {user ? (
              <>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setJoinAs("contributor")}
                    className={`w-full flex items-start gap-4 p-4 rounded-md border text-left transition-colors ${
                      joinAs === "contributor"
                        ? "border-primary bg-primary/5"
                        : "border-border"
                    }`}
                    data-testid="option-contributor"
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${
                        joinAs === "contributor"
                          ? "bg-primary/10"
                          : "bg-muted"
                      }`}
                    >
                      <HandHeart
                        className={`h-5 w-5 ${
                          joinAs === "contributor"
                            ? "text-primary"
                            : "text-muted-foreground"
                        }`}
                      />
                    </div>
                    <div>
                      <p className="font-medium">Contributor</p>
                      <p className="text-sm text-muted-foreground">
                        Help fund healthcare for wallet members
                      </p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setJoinAs("beneficiary")}
                    className={`w-full flex items-start gap-4 p-4 rounded-md border text-left transition-colors ${
                      joinAs === "beneficiary"
                        ? "border-primary bg-primary/5"
                        : "border-border"
                    }`}
                    data-testid="option-beneficiary"
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${
                        joinAs === "beneficiary"
                          ? "bg-primary/10"
                          : "bg-muted"
                      }`}
                    >
                      <Heart
                        className={`h-5 w-5 ${
                          joinAs === "beneficiary"
                            ? "text-primary"
                            : "text-muted-foreground"
                        }`}
                      />
                    </div>
                    <div>
                      <p className="font-medium">Beneficiary</p>
                      <p className="text-sm text-muted-foreground">
                        Receive healthcare funding from this wallet
                      </p>
                    </div>
                  </button>
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  disabled={!joinAs || joinMutation.isPending}
                  onClick={handleJoin}
                  data-testid="button-join-wallet"
                >
                  {joinMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Join Wallet
                    </>
                  )}
                </Button>
              </>
            ) : (
              <div className="space-y-4 text-center">
                <p className="text-muted-foreground">
                  Create an account or log in to accept this invitation and
                  join the wallet.
                </p>
                <Button
                  className="w-full"
                  size="lg"
                  onClick={() =>
                    setLocation(
                      `/auth?returnTo=/wallet-invite/${token}`
                    )
                  }
                  data-testid="button-auth-redirect"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create Account to Join
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    setLocation(
                      `/auth?returnTo=/wallet-invite/${token}`
                    )
                  }
                  data-testid="button-login-redirect"
                >
                  Already have an account? Log In
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center space-y-3">
          <Button
            variant="ghost"
            onClick={() => setLocation("/")}
            data-testid="link-back-home"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <p className="text-sm text-muted-foreground">
            Powered by{" "}
            <span className="font-semibold text-foreground">HealthWallet</span>
          </p>
        </div>
      </div>
    </div>
  );
}
